import _taggedTemplateLiteral from '../../../node_modules/@babel/runtime/helpers/taggedTemplateLiteral.js';
import { spaceMixin, token, Paragraph, Flex, TooltipPopper, Tooltip, Spinner, Spacer, Text, Divider } from '@zillow/constellation';
import PropTypes from 'prop-types';
import { memo } from 'react';
import styled, { css } from 'styled-components';
import { dollar } from '../../../formatters.js';
import { trackGAMortgageEvent } from '../analytics.js';
import calculatorImageWebp from '../assets/calculator.js';
import PrimaryCta from './PrimaryCta.js';
import jsxRuntime from '../../../node_modules/react/jsx-runtime.js';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
var BREAKDOWNS = [{
  title: 'Principal + interest',
  id: 'monthlyPI'
}, {
  title: 'Property taxes',
  id: 'monthlyPropertyTax'
}, {
  title: "Homeowner's insurance",
  id: 'monthlyHomeownersInsurance'
}, {
  title: 'Private mortgage insurance',
  id: 'monthlyPMI'
}];
var commonWrapperCss = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    width: 100%;\n    display: flex;\n    flex-direction: column;\n    padding: ", " 0;\n    border: 1px solid ", ";\n    border-radius: ", ";\n"])), spaceMixin('md'), token('colors.borderLight'), token('radii.md'));
var Wrapper = styled.output(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", "\n"])), commonWrapperCss);
var DefaultWrapper = styled.output(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    ", "\n    align-items: center;\n    justify-content: center;\n"])), commonWrapperCss);
var StyledParagraph = styled(Paragraph)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    text-align: center;\n"])));
var CalculatorImage = styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    background-image: url(\"", "\");\n    background-repeat: no-repeat;\n    background-position: center;\n    background-size: contain;\n    height: 212px;\n    width: 280px;\n"])), calculatorImageWebp);
var BreakdownRow = styled(Flex)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    ", "\n"])), function (_ref) {
  var isEven = _ref.isEven;
  return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n        background-color: ", ";\n    "])), isEven ? token('colors.backgroundLight') : 'none');
});
var StyledTooltipPopper = styled(TooltipPopper)(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    background-color: ", ";\n"])), token('colors.brand'));
var PaymentTooltip = /*#__PURE__*/jsxRuntime.jsx(StyledTooltipPopper, {
  onOpen: function onOpen() {
    trackGAMortgageEvent('Budget Calculator: Interaction', 'Tooltip');
  },
  triggered: /*#__PURE__*/jsxRuntime.jsx(Tooltip, {
    body: /*#__PURE__*/jsxRuntime.jsxs(jsxRuntime.Fragment, {
      children: [/*#__PURE__*/jsxRuntime.jsx(Paragraph, {
        children: "All calculations are estimates and provided for informational purposes only. Actual amounts may vary."
      }), /*#__PURE__*/jsxRuntime.jsxs(Paragraph, {
        marginTop: "xs",
        children: ["Mortgage interest rates are dependent on a number of factors, including credit score, down payment, and repayment length. Certain mortgage lenders with which we have lead or other similar arrangements have provided interest rates as of ", new Date().toLocaleDateString(), "; the Estimated Payment is an average of those rates."]
      })]
    })
  })
});
var Outputs = function Outputs(_ref2) {
  var outputs = _ref2.outputs,
    isCalculating = _ref2.isCalculating;
  if (isCalculating) {
    return /*#__PURE__*/jsxRuntime.jsxs(DefaultWrapper, {
      children: [/*#__PURE__*/jsxRuntime.jsx(Spinner, {
        size: "lg"
      }), /*#__PURE__*/jsxRuntime.jsx(StyledParagraph, {
        fontType: "bodySmall",
        marginTop: "md",
        fontColor: "textMedium",
        children: "One moment while we calculate your results..."
      })]
    });
  }
  if (outputs) {
    return /*#__PURE__*/jsxRuntime.jsxs(Wrapper, {
      children: [/*#__PURE__*/jsxRuntime.jsxs(Flex, {
        display: "flex",
        flexDirection: "column",
        flex: "1",
        children: [/*#__PURE__*/jsxRuntime.jsxs(Spacer, {
          marginX: "md",
          children: [/*#__PURE__*/jsxRuntime.jsxs(Paragraph, {
            fontType: "bodySmallHeading",
            children: ["You can afford a home between ", /*#__PURE__*/jsxRuntime.jsx("br", {}), ' ', /*#__PURE__*/jsxRuntime.jsxs(Text, {
              fontType: "h4",
              children: [dollar(outputs === null || outputs === void 0 ? void 0 : outputs.lowerBoundHomePrice), " - ", dollar(outputs === null || outputs === void 0 ? void 0 : outputs.upperBoundHomePrice)]
            })]
          }), /*#__PURE__*/jsxRuntime.jsx(Divider, {
            marginY: "sm"
          }), /*#__PURE__*/jsxRuntime.jsxs(Paragraph, {
            fontType: "bodySmallHeading",
            children: ["Average monthly payment ", /*#__PURE__*/jsxRuntime.jsx("br", {}), " ", /*#__PURE__*/jsxRuntime.jsx(Text, {
              fontType: "h4",
              children: dollar(outputs === null || outputs === void 0 ? void 0 : outputs.monthlyPayment)
            })]
          })]
        }), /*#__PURE__*/jsxRuntime.jsxs(Spacer, {
          marginTop: "lg",
          children: [/*#__PURE__*/jsxRuntime.jsxs(Paragraph, {
            fontType: "bodySmallHeading",
            marginBottom: "sm",
            paddingX: "md",
            children: ["Payment breakdown ", PaymentTooltip]
          }), BREAKDOWNS.map(function (_ref3, index) {
            var title = _ref3.title,
              id = _ref3.id;
            return /*#__PURE__*/jsxRuntime.jsxs(BreakdownRow, {
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              isEven: index % 2 === 0,
              paddingX: "md",
              paddingY: "xs",
              children: [/*#__PURE__*/jsxRuntime.jsx(Text, {
                fontType: "bodySmall",
                children: title
              }), /*#__PURE__*/jsxRuntime.jsx(Text, {
                fontType: "bodySmallHeading",
                children: dollar(outputs[id])
              })]
            }, id);
          })]
        })]
      }), /*#__PURE__*/jsxRuntime.jsx(Spacer, {
        marginTop: "md",
        children: /*#__PURE__*/jsxRuntime.jsx(PrimaryCta, {
          outputs: outputs
        })
      })]
    });
  }
  return /*#__PURE__*/jsxRuntime.jsxs(DefaultWrapper, {
    children: [/*#__PURE__*/jsxRuntime.jsx(CalculatorImage, {}), /*#__PURE__*/jsxRuntime.jsx(StyledParagraph, {
      fontType: "bodySmall",
      fontColor: "textMedium",
      marginX: "md",
      marginTop: "md",
      children: "Enter your information to calculate an estimated budget."
    })]
  });
};
Outputs.propTypes = {
  outputs: PropTypes.object,
  isCalculating: PropTypes.bool
};
Outputs.defaultProps = {
  isCalculating: false
};
var Outputs$1 = /*#__PURE__*/memo(Outputs);

export { Outputs$1 as default };
//# sourceMappingURL=Outputs.js.map
